import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import type { Profile } from "../../lib/database.types";
import { supabase } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";
import { disablePush, enablePush, isPushEnabledHere } from "../../lib/push";
import { pushReadiness } from "../../lib/platform";
import { usePendingCheckIns } from "../checkins/api";
import { flushOutbox } from "../../lib/outbox";
import { BackButton } from "../../components/AppShell";
import { Button, Card, Spinner } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { errorMessage } from "../../lib/errors";

export function SettingsPage() {
  const { profile } = useAuth();

  // Wait for the profile, then key the form on it: the editable fields get
  // their initial values as props instead of being synced inside an effect.
  if (!profile) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  return <Settings key={profile.id} profile={profile} />;
}

function Settings({ profile }: { profile: Profile }) {
  const { userId, session } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const pending = usePendingCheckIns();

  const [displayName, setDisplayName] = useState(profile.display_name);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    void isPushEnabledHere().then(setPushOn);
  }, []);

  const dirty = displayName.trim() !== profile.display_name;

  async function saveProfile() {
    if (!userId || !displayName.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim() })
      .eq("id", userId);
    setSaving(false);

    if (error) {
      toast(error.message, "error");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: keys.profiles });
    toast("Saved", "success");
  }

  async function savePassword() {
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSavingPassword(false);
    if (error) {
      toast(error.message, "error");
      return;
    }
    setPassword("");
    toast("Password saved", "success");
  }

  async function togglePush() {
    if (!userId) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast("Notifications off on this device");
      } else {
        const granted = await enablePush(userId);
        setPushOn(granted);
        toast(
          granted ? "Notifications on 🔔" : "Permission denied",
          granted ? "success" : "info",
        );
      }
    } catch (error) {
      toast(errorMessage(error, "Could not change notifications"), "error");
    } finally {
      setPushBusy(false);
    }
  }

  const readiness = pushReadiness();
  const canTogglePush =
    readiness.state !== "needs-install" && readiness.state !== "unsupported";

  return (
    <div className="pt-safe">
      <header className="flex items-center gap-2 pb-4">
        <BackButton onClick={() => navigate(-1)} />
        <h1 className="text-xl">Settings</h1>
      </header>

      <div className="flex flex-col gap-3">
        <Card className="gap-3 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="display_name" className="text-xs opacity-60">
              display name
            </label>
            <input
              id="display_name"
              value={displayName}
              maxLength={40}
              onChange={(event) => setDisplayName(event.target.value)}
              className="input"
            />
          </div>

          <p className="text-[13px] text-neutral-500">
            To change your photo, tap your avatar on your profile.
          </p>

          <Button
            onClick={saveProfile}
            disabled={!dirty || saving}
            className="btn-block"
          >
            {saving ? <Spinner className="border-t-current" /> : "Save"}
          </Button>
        </Card>

        <Card className="gap-3 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="new_password" className="text-xs opacity-60">
              set a password
            </label>
            <input
              id="new_password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="8+ characters"
              className="input"
            />
            <p className="text-[13px] text-neutral-500">
              Sign in with your email and this password instead of an email link.
            </p>
          </div>
          <Button
            onClick={savePassword}
            disabled={password.length < 8 || savingPassword}
            className="btn-block"
          >
            {savingPassword ? <Spinner className="border-t-current" /> : "Save password"}
          </Button>
        </Card>

        <Card className="gap-2 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Notifications</p>
              <p className="text-[13px] opacity-60">
                {readiness.state === "needs-install"
                  ? "Add Our Pact to your home screen first — iOS only allows push for installed apps."
                  : readiness.state === "unsupported"
                    ? "This browser doesn't support web push."
                    : pushOn
                      ? "On for this device."
                      : "Evening reminders and pokes from the group."}
              </p>
            </div>
            {canTogglePush && (
              <Button
                variant={pushOn ? "secondary" : "primary"}
                onClick={togglePush}
                disabled={pushBusy || pushOn === null}
                className="shrink-0 px-3.5 py-[7px] text-xs"
              >
                {pushBusy ? (
                  <Spinner className="border-t-current" />
                ) : pushOn ? (
                  "turn off"
                ) : (
                  "turn on"
                )}
              </Button>
            )}
          </div>
          <p className="border-t border-neutral-300 pt-2.5 text-[11px] opacity-50">
            Notifications are per-device. Turning them on here doesn't affect
            your other phones.
          </p>
        </Card>

        <Card className="gap-2 p-4">
          <p className="text-sm font-semibold">Sync</p>
          <p className="text-[13px] opacity-60">
            {pending.length === 0
              ? "Everything is saved to the server."
              : `${pending.length} check-in${pending.length === 1 ? "" : "s"} waiting on this device.`}
          </p>
          {pending.length > 0 && (
            <div className="flex">
              <Button
                variant="secondary"
                className="px-3.5 py-[7px] text-xs"
                onClick={() => void flushOutbox()}
              >
                sync now
              </Button>
            </div>
          )}
        </Card>

        <Card className="gap-1 p-4">
          <p className="text-sm font-semibold">Account</p>
          <p className="truncate text-[13px] opacity-60">
            {session?.user.email}
          </p>
          <p className="text-[11px] opacity-50">Timezone: {profile.timezone}</p>
          <div className="mt-2 flex">
            <Button
              variant="danger"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate("/");
              }}
            >
              sign out
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
